import React, { useState, useEffect } from 'react';
import { Loader2, Maximize, Minimize } from 'lucide-react';

export default function Board() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stopName, setStopName] = useState('');
  const [routeName, setRouteName] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const stopId = urlParams.get('stop_id') || 'place-mvbcl'; // Default: Maverick
  const directionId = urlParams.get('direction_id') || '0'; // Default: direction 0

  useEffect(() => {
    // Update clock every second
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Handle fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      clearInterval(clockInterval);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        
        // Fetch predictions with stop and route information
        const response = await fetch(
          `https://api-v3.mbta.com/predictions?filter[stop]=${stopId}&filter[route]=Blue&filter[direction_id]=${directionId}&include=stop,route,trip&sort=departure_time`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch MBTA data');
        }

        const data = await response.json();
        
        // Extract stop and route names from included data
        if (data.included) {
          const stop = data.included.find(item => item.type === 'stop');
          const route = data.included.find(item => item.type === 'route');
          
          if (stop) setStopName(stop.attributes.name);
          if (route) setRouteName(route.attributes.long_name);
        }

        // Filter out predictions with null departure_time, then get next 2
        const validPredictions = data.data.filter(p => p.attributes.departure_time !== null);
        const nextTrains = validPredictions.slice(0, 2);
        setPredictions(nextTrains);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
    
    // Refresh every 10 seconds
    const interval = setInterval(fetchPredictions, 10000);
    
    return () => clearInterval(interval);
  }, [stopId, directionId]);

  const getMinutesUntilArrival = (prediction) => {
    // Check for status field first (e.g., Commuter Rail)
    if (prediction.attributes.status) {
      return prediction.attributes.status;
    }
    
    // Use arrival_time if available, otherwise departure_time
    const timeToUse = prediction.attributes.arrival_time || prediction.attributes.departure_time;
    
    if (!timeToUse) return 'BRD'; // Boarding
    
    const now = new Date();
    const arrival = new Date(timeToUse);
    const diffSeconds = Math.floor((arrival - now) / 1000);
    
    if (diffSeconds < 30) return 'ARR'; // Arriving (less than 30 seconds)
    
    const diffMinutes = Math.round(diffSeconds / 60);
    if (diffMinutes < 1) return 'ARR'; // Less than 1 minute
    if (diffMinutes === 1) return '1 min';
    return `${diffMinutes} min`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  };

  if (loading && predictions.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <Loader2 className="w-16 h-16 text-[#ff8c00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-[#0a0a0a] text-[#ff8c00] overflow-hidden flex flex-col font-mono">
      {/* Header */}
      <div className="px-8 py-4 border-b-2 border-[#ff8c00]/30">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-2xl font-bold tracking-wider">{stopName || 'MAVERICK'}</div>
            <div className="text-sm text-[#ff8c00]/70 mt-1">{routeName || 'Blue Line'}</div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-[#ff8c00]/10 rounded-lg transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
            </button>
            <div className="text-right">
              <div className="text-3xl font-bold tabular-nums">{formatTime(currentTime)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Train Arrivals */}
      <div className="flex-1 flex flex-col justify-center px-8 py-6 space-y-6">
        {error ? (
          <div className="text-center text-2xl">
            <div className="text-red-500 mb-2">ERROR</div>
            <div className="text-lg text-[#ff8c00]/70">{error}</div>
          </div>
        ) : predictions.length === 0 ? (
          <div className="text-center text-3xl font-bold">
            NO TRAINS SCHEDULED
          </div>
        ) : (
          predictions.map((prediction, index) => {
            const timeUntil = getMinutesUntilArrival(prediction);
            const destination = prediction.attributes.direction_id === 0 ? 'BOWDOIN' : 'WONDERLAND';

            return (
              <div 
                key={prediction.id} 
                className="flex justify-between items-center border-2 border-[#ff8c00]/40 rounded-lg px-8 py-6 bg-[#0a0a0a]"
                style={{
                  boxShadow: '0 0 20px rgba(255, 140, 0, 0.15)'
                }}
              >
                <div className="flex items-center space-x-6">
                  <div className="text-6xl font-bold w-20 text-center">{index + 1}</div>
                  <div>
                    <div className="text-xl text-[#ff8c00]/70 mb-1">TO</div>
                    <div className="text-4xl font-bold tracking-wide">{destination}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div 
                    className="text-7xl font-bold tabular-nums"
                    style={{
                      textShadow: '0 0 30px rgba(255, 140, 0, 0.5)',
                      animation: timeUntil === 'ARR' || timeUntil === 'BRD' ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }}
                  >
                    {timeUntil}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-3 border-t-2 border-[#ff8c00]/30 text-center text-sm text-[#ff8c00]/60">
        Live MBTA Predictions • Updates every 10 seconds
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );
}
